import { LANG_QUERY_PARAM } from '@core/language/lang-param';
import { resolveDeepLink, DeepLinkRoute } from './deep-link';
import { APP_PATHS } from './app-routes.enum';

/** A resolved legacy handoff: where to send the visit. */
export interface V1LegacyHandoff extends DeepLinkRoute {
  /** The normalised v1 path that matched (logging only). */
  from: string;
}

/**
 * v1 ENTRY path → the canonical link shape its surface now lives at.
 *
 * Deliberately EXACT paths only (no sub-paths): `/appointments/session/{id}` and
 * `/appointments/session/ai/{id}` are still real v1 screens — the v1 home flow
 * itself navigates to them — and must keep working untouched. They do: the app-level
 * compat redirect forwards them to `/v1/appointments/session/{id}`.
 *
 * The values are LINK shapes rather than ready-made router commands so the care
 * `?screen=` aliases stay owned by ONE resolver ({@link resolveDeepLink}); a legacy
 * link and a server-authored notification link therefore land on exactly the same
 * screen.
 */
const LEGACY_PATH_MAP: Readonly<Record<string, string>> = {
  /** v1 «مواعيدي» listing → Care CR5 «جلساتي» (my sessions). */
  appointments: `${APP_PATHS.CARE}/sessions`,
  /** v1 booking entry (doctor picker) → Care CR2 «المستشارون» (the directory). */
  'book-appointment': `${APP_PATHS.CARE}/directory`,
};

/**
 * The query params that mark a URL as an EXTERNAL legacy deep link (app / SMS /
 * email / campaign), as opposed to plain in-portal v1 navigation:
 *
 *   · `lang`       — the sender pins the reader's language (`?lang=ar` · `?lang=en`)
 *   · `isEmergency`
 *   · `isMedicine` — the two historical urgent-care shortcuts
 *
 * The gate matters: v1's own header/menu links to `/appointments` and
 * `/book-appointment` carry NONE of these, so v1-internal navigation is left
 * completely alone and only a real inbound deep link is handed off to /v2.
 */
const LEGACY_MARKERS = [LANG_QUERY_PARAM, 'isEmergency', 'isMedicine'] as const;

/**
 * v1-legacy-deep-link — resolve a LEGACY v1 entry URL to its redesigned destination.
 *
 * Old links still point at v1 paths that the redesign has replaced. Rather than
 * keep those screens alive in two places, an inbound legacy link is handed off to
 * the surface that owns the journey now:
 *
 *   /appointments?isEmergency=true&lang=ar → `/care?screen=sessions`  (CR5)
 *   /book-appointment?lang=en              → `/care?screen=directory` (CR2)
 *
 * This runs from the app root's CONSTRUCTOR and navigates on the first `queryParams`
 * emission, which suppresses the router's initial navigation — so it wins over the
 * app-level `/appointments` → `/v1/appointments` compat redirect, and a marked deep
 * link never flashes the legacy screen on its way to the redesigned one.
 *
 * DESTINATION ONLY — `?lang=` is NOT resolved here. It is not specific to legacy
 * links (any url may carry it), so ONE owner applies it for every visit:
 * `App.applyEntryLangParam`, which runs before this handoff navigates.
 * That keeps this a PURE, SSR-safe string function (no storage, no router, no
 * platform access) that can be called from a constructor and unit-tested.
 *
 * Returns `null` for every other URL — including a v1 sub-path and a marker-less
 * `/appointments` clicked from inside v1 — so the caller does nothing at all.
 */
export function resolveV1LegacyHandoff(
  pathname: string | null | undefined,
  search: string | null | undefined,
): V1LegacyHandoff | null {
  const from = normalizePath(pathname);
  const link = LEGACY_PATH_MAP[from];
  if (!link) return null;

  // Only a marked (external) deep link is handed off — see LEGACY_MARKERS.
  const params = new URLSearchParams(search ?? '');
  if (!LEGACY_MARKERS.some((marker) => params.has(marker))) return null;

  const target = resolveDeepLink(link);
  if (!target) return null; // unreachable for the table above; keeps the caller honest

  return { ...target, from };
}

/** `/Appointments/?x=1` → `appointments` (query/hash stripped, unslashed, lower-cased). */
function normalizePath(pathname: string | null | undefined): string {
  if (!pathname) return '';
  return pathname.split(/[?#]/)[0]!.replace(/^\/+|\/+$/g, '').trim().toLowerCase();
}
